import styled from "styled-components";
import { colors } from "../../styles/colors";
import { typography } from "../../styles/typography";

export const LinksPage = styled("main")`
  position: relative;
  width: 100%;
  min-height: 100vh;
  margin-top: 0;
  padding: 2.5rem 1.5rem 3rem;
  display: flex;
  align-items: center;
  flex-direction: column;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% -12%, color-mix(in srgb, ${colors.secondary.main} 15%, transparent), transparent 32rem),
    linear-gradient(145deg, ${colors.primary.dark} 0%, ${colors.background.primary} 58%, ${colors.primary.main} 100%);
  color: ${colors.text.primary};

  &:before,
  &:after {
    content: "";
    position: absolute;
    width: 24rem;
    height: 24rem;
    border: 1px solid color-mix(in srgb, ${colors.secondary.main} 12%, transparent);
    border-radius: 50%;
    pointer-events: none;
  }

  &:before {
    top: -14rem;
    left: -12rem;
  }

  &:after {
    right: -15rem;
    bottom: 2rem;
  }

  h1 {
    width: 100%;
    max-width: 31rem;
    margin: 1.5rem 0 0.75rem;
    color: ${colors.text.primary};
    font-family: ${typography.fontFamily.bold};
    font-size: clamp(1.8rem, 4.2vw, 2.65rem);
    line-height: 1.1;
    letter-spacing: 0;
    text-align: center;
  }

  @media (max-width: 600px) {
    padding: 2rem 1rem;
  }
`;

export const Brand = styled("div")`
  width: min(17rem, 85vw);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BrandLogo = styled("img")`
  display: block;
  width: 100%;
  max-height: 4.5rem;
  object-fit: contain;
  filter:
    drop-shadow(0 0 0.12rem ${colors.secondary.main})
    drop-shadow(0 0 0.45rem color-mix(in srgb, ${colors.secondary.main} 30%, transparent))
    drop-shadow(0 0 1rem color-mix(in srgb, ${colors.secondary.main} 20%, transparent));
`;

export const Tagline = styled("span")`
  color: ${colors.secondary.main};
  font-family: ${typography.fontFamily.bold};
  font-size: 0.75rem;
  letter-spacing: 0.26rem;
`;

export const Bio = styled("p")`
  max-width: 30rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: 1rem;
  line-height: 1.55;
  text-align: center;
`;

export const LinksColumn = styled("div")`
  width: 100%;
  min-width: 0;
  max-width: 34rem;
  display: grid;
  gap: 0.75rem;
  margin-top: 2rem;
`;

export const LinkCard = styled("a")`
  position: relative;
  z-index: 1;
  min-height: 4.75rem;
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid color-mix(in srgb, ${colors.text.primary} 14%, transparent);
  border-radius: 0.75rem;
  background: color-mix(in srgb, ${colors.text.primary} 5.5%, transparent);
  color: ${colors.text.primary};
  overflow: hidden;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    border-color: ${colors.secondary.main};
    background: color-mix(in srgb, ${colors.secondary.main} 10%, transparent);
    color: ${colors.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${colors.secondary.main};
    outline-offset: 3px;
  }
`;

export const LinkIcon = styled("span")`
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  display: grid;
  place-items: center;
  border-radius: 0.5rem;
  background: color-mix(in srgb, ${colors.secondary.main} 10%, transparent);
  color: ${colors.secondary.main};
`;

export const LinkText = styled("span")`
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.2rem;

  span {
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.78rem;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }
`;

export const LinkLabel = styled("strong")`
  font-family: ${typography.fontFamily.bold};
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
`;

export const ActionIcon = styled("span")`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  color: rgba(255, 255, 255, 0.52);
`;

export const SocialLinks = styled("nav")`
  position: relative;
  z-index: 1;
  display: flex;
  gap: 0.7rem;
  margin-top: 1.75rem;
`;

export const SocialLink = styled("a")`
  width: 2.5rem;
  height: 2.5rem;
  display: grid;
  place-items: center;
  border: 1px solid color-mix(in srgb, ${colors.text.primary} 16%, transparent);
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.72);
  transition: color 180ms ease, border-color 180ms ease, transform 180ms ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    border-color: ${colors.secondary.main};
    color: ${colors.secondary.main};
  }
`;

export const Footer = styled("small")`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 2rem;
  color: rgba(255, 255, 255, 0.34);
  font-size: 0.65rem;
  letter-spacing: 0.13rem;
`;

export const LanguageSwitcher = styled("nav")`
  display: flex;
  gap: 0.25rem;
  letter-spacing: 0;
`;

export const LanguageButton = styled("button")<{ $active: boolean }>`
  min-width: 2.25rem;
  padding: 0.3rem 0.45rem;
  border: 1px solid
    ${({ $active }) =>
      $active
        ? colors.secondary.main
        : "rgba(255, 255, 255, 0.18)"};
  border-radius: 0.35rem;
  background: transparent;
  color: ${({ $active }) =>
    $active ? colors.secondary.main : "rgba(255, 255, 255, 0.52)"};
  cursor: pointer;
  font-family: ${typography.fontFamily.bold};
  font-size: 0.62rem;
  letter-spacing: 0.04rem;
  transition: color 180ms ease, border-color 180ms ease, background 180ms ease;

  &:hover,
  &:focus-visible {
    border-color: ${colors.secondary.main};
    background: color-mix(in srgb, ${colors.secondary.main} 10%, transparent);
    color: ${colors.secondary.main};
  }

  &:focus-visible {
    outline: 2px solid ${colors.secondary.main};
    outline-offset: 2px;
  }
`;